import React, { useEffect, useState } from 'react';
import Modal from 'components/elements/Modal';
import * as yup from 'yup';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import CancelIcon from 'assets/Icons/modals/CancelIcon';
import CardsIcon from 'assets/Icons/sidebarIcons/CardsIcon';
import { useQuery } from 'react-query';
import LoadingSpinner from 'components/elements/Spinner/LoadingSpinner';
import SelectEntity from 'components/structure/Common/ListsEntity/SelectEntity';
import CardService, { CardProps } from 'services/hooks/card-service';
import OrganizationService, { OrganizationProps } from 'services/hooks/organization-service';
import BuildingService, { BuildingProps } from 'services/hooks/building-service';
import Input from 'components/elements/Form/Input/input';
import GeneratorInput from 'components/elements/Form/Input/GeneratorInput';
import useSaveCard from './crud/useSave';
import PrimaryButton from 'components/elements/Form/button/primaryButton';
import SelectInput from 'components/elements/Form/select/selectInput';
import OccupantService, { OccupantProps } from 'services/hooks/occupant-service';
import {
  assaGuestCardTypes,
  cardFormatLabels,
  CardFormat,
  defaultDateTimeUTCValue,
  assaCardTypes,
  AssaGuestCardTypeValue,
  AccessIntegrationName,
  AssaGuestOverrideCardTypeValue,
  AssaGuestJoinerCardTypeValue,
  GetNonServerErrorMessage
} from 'utils/constants';
import SeamAssaVisionlineService, {
  SeamVisionlineDoorsProps
} from 'services/hooks/seam-assa-visionline-service';
import { toast } from 'react-toastify';
import {
  generateApiFailErrorMessage,
  generateSaveErrorMessage,
  generateSaveSuccessMessage,
  generateUpdateErrorMessage,
  generateUpdateSuccessMessage
} from 'utils/helpers';
import { formatRFC3339 } from 'date-fns';
import TimezoneDateTimeRangePicker from 'components/elements/Form/Input/TimezoneDateTimeRangePicker';
import moment from 'moment-timezone';
import NestedAntdMultiSelect, {
  DataNode
} from 'components/elements/Form/select/NestedAntdMultiSelect';
import IntegrationService, { IntegrationProps } from 'services/hooks/integration-service';
import FormatVisionlineDoorsTreeData from 'utils/visionlineDoorsFormatter';
import LatchService, { LatchDoorProps } from 'services/hooks/latch-service';
import FormatLatchDoorsTreeData from 'utils/latchDoorsFormatter';

type CreateProps = {
  entityName: string;
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  setEditUUID?: React.Dispatch<React.SetStateAction<string>>;
  createQuery: any;
  updateQuery?: any;
  editQuery?: any;
  editUUID?: string;
  queryName: string;
};

export default function Save({
  entityName,
  showModal,
  setEditUUID,
  setShowModal,
  createQuery,
  updateQuery,
  editQuery,
  queryName,
  editUUID
}: CreateProps) {
  const [getcardResult, setGetcardResult] = useState<CardProps | null>(null);
  const [buildingInfo, setbuildingInfo] = useState<BuildingProps | null>(null);
  const [visionlineDoorsData, setVisionlineDoorsData] = useState<SeamVisionlineDoorsProps[] | null>(
    null
  );
  const [doorOptions, setDoorOptions] = useState<DataNode[]>([]);
  const [selectedCardFormat, setSelectedCardFormat] = useState<CardFormat | null>(null);
  const [buildingTimezone, setBuildingTimezone] = useState<any>(null);
  const [acsSystemId, setAcsSystemId] = useState<string | undefined>(undefined);
  const [isGuestAssaCardType, setIsGuestAssaCardType] = useState<boolean>(false);
  const [disabled, setDisabled] = useState(false);
  const [latchDoorsData, setLatchDoorsData] = useState<LatchDoorProps | null>(null);
  const [latchDoorOptions, setLatchDoorOptions] = useState<DataNode[]>([]);
  const [isNoneIntegration, setIsNoneIntegration] = useState<boolean>(false);

  // validation
  const CreateCardSchema = yup.object().shape({
    organizationUuid: yup.string().required('Organization is required'),
    format: yup.string().required('Card Format is required'),
    badgeId: yup.string().when([], {
      is: () => selectedCardFormat === CardFormat.W26,
      then: yup.string().required('Badge Id is required'),
      otherwise: yup.string().notRequired()
    }),
    facilityCode: yup.string().when([], {
      is: () => selectedCardFormat === CardFormat.W26,
      then: yup.string().required('Facility Code is required'),
      otherwise: yup.string().notRequired()
    }),
    doorIds: yup.array().when([], {
      is: () => selectedCardFormat === CardFormat.AVL,
      then: yup.array().min(1, 'Door is required'),
      otherwise: yup.array().notRequired()
    }),
    mobileUserUuid: yup.string().when([], {
      is: () => selectedCardFormat === CardFormat.AVL || selectedCardFormat === CardFormat.LATCH,
      then: yup.string().required('Occupant is required'),
      otherwise: yup.string().notRequired()
    }),
    assaCardType: yup.string().when([], {
      is: () => selectedCardFormat === CardFormat.AVL,
      then: yup.string().required('Assa card type is required'),
      otherwise: yup.string().notRequired()
    }),
    assaGuestCardType: yup.string().when([], {
      is: () => selectedCardFormat === CardFormat.AVL && isGuestAssaCardType,
      then: yup.string().required('Guest card type is required'),
      otherwise: yup.string().notRequired()
    }),
    startDate: yup.date().when([], {
      is: () => selectedCardFormat === CardFormat.AVL,
      then: yup.date().required('Check-in date & time is required'),
      otherwise: yup.date().notRequired()
    }),
    endDate: yup.date().when([], {
      is: () => selectedCardFormat === CardFormat.AVL,
      then: yup
        .date()
        .required('Check-out date & time is required')
        .test('is-greater', 'Check-out time must be after check-in time', function (value) {
          const { startDate } = this.parent;
          return startDate && value && value > startDate;
        }),
      otherwise: yup.date().notRequired()
    }),
    latchDoorIds: yup.array().when([], {
      is: () => selectedCardFormat === CardFormat.LATCH,
      then: yup.array().min(1, 'Door is required'),
      otherwise: yup.array().notRequired()
    }),
    latchStartDate: yup.date().when([], {
      is: () => selectedCardFormat === CardFormat.LATCH,
      then: yup.date().required('Check-in date & time is required'),
      otherwise: yup.date().notRequired()
    }),
    latchEndDate: yup.date().when([], {
      is: () => selectedCardFormat === CardFormat.LATCH,
      then: yup
        .date()
        .required('Check-out date & time is required')
        .test('is-greater', 'Check-out time must be after check-in time', function (value) {
          const { latchStartDate } = this.parent;
          return latchStartDate && value && value > latchStartDate;
        }),
      otherwise: yup.date().notRequired()
    })
  });

  // Fetch user specific Card data
  const { isSuccess, data: cardInfo } = useQuery<CardProps, Error>(
    'card',
    () => editQuery(editUUID),
    {
      enabled: editUUID ? true : false,
      onError: () => {
        toast.error(generateApiFailErrorMessage('Card'));
      }
    }
  );

  const methods = useForm({
    defaultValues: getcardResult
      ? {
          uuid: getcardResult?.uuid,
          description: getcardResult?.description,
          buildingUuid: getcardResult?.buildingUuid,
          organizationUuid: getcardResult?.organizationUuid,
          badgeId: getcardResult?.badgeId,
          facilityCode: getcardResult?.facilityCode,
          status: getcardResult?.status,
          mobileUserUuid: getcardResult?.mobileUserUuid,
          format: getcardResult?.format,
          doorIds: getcardResult?.doorIds,
          startDate:
            getcardResult?.startDate !== defaultDateTimeUTCValue
              ? new Date(getcardResult?.startDate)
              : moment.tz(new Date(), buildingTimezone).toDate(),
          endDate:
            getcardResult?.endDate !== defaultDateTimeUTCValue
              ? new Date(getcardResult?.endDate)
              : moment.tz(new Date(), buildingTimezone).toDate(),
          latchDoorIds: getcardResult?.doorIds,
          latchStartDate:
            getcardResult?.startDate !== defaultDateTimeUTCValue
              ? new Date(getcardResult?.startDate)
              : moment.tz(new Date(), buildingTimezone).toDate(),
          latchEndDate:
            getcardResult?.endDate !== defaultDateTimeUTCValue
              ? new Date(getcardResult?.endDate)
              : moment.tz(new Date(), buildingTimezone).toDate()
        }
      : {
          uuid: '',
          description: '',
          buildingUuid: '',
          organizationUuid: '',
          badgeId: '',
          facilityCode: '',
          status: '',
          mobileUserUuid: '',
          format: '',
          doorIds: [],
          startDate: moment.tz(new Date(), buildingTimezone).toDate(),
          endDate: moment.tz(new Date(), buildingTimezone).toDate(),
          assaCardType: '',
          assaGuestCardType: '',
          latchDoorIds: [],
          latchStartDate: moment.tz(new Date(), buildingTimezone).toDate(),
          latchEndDate: moment.tz(new Date(), buildingTimezone).toDate()
        },
    resolver: yupResolver(CreateCardSchema)
  });

  const orgUuid = methods.watch('organizationUuid');
  const { data: orgData } = useQuery<OrganizationProps, Error>(
    ['organization', orgUuid],
    () => OrganizationService.findOrgById(orgUuid),
    {
      enabled: orgUuid ? true : false,
      onError: () => {
        toast.error(generateApiFailErrorMessage('Organization'));
      }
    }
  );

  const { data: buildingData } = useQuery<BuildingProps, Error>(
    ['building', orgData?.buildingUuid],
    () => BuildingService.findBuildingById(orgData?.buildingUuid),
    {
      enabled: orgData?.buildingUuid ? true : false,
      onError: () => {
        toast.error(generateApiFailErrorMessage('Building'));
      }
    }
  );

  const { data: cardsData } = useQuery<CardProps[], Error>(
    'cards',
    () => CardService.findAllCards(),
    {
      enabled: orgData?.buildingUuid ? true : false,
      onError: () => {
        toast.error(generateApiFailErrorMessage('Cards'));
      }
    }
  );

  useEffect(() => {
    if (cardInfo && editUUID) {
      setGetcardResult(cardInfo);
      methods.reset({
        uuid: cardInfo?.uuid,
        description: cardInfo?.description,
        buildingUuid: cardInfo?.buildingUuid,
        organizationUuid: cardInfo?.organizationUuid,
        badgeId: cardInfo?.badgeId,
        facilityCode: cardInfo?.facilityCode,
        status: cardInfo?.status,
        mobileUserUuid: cardInfo?.mobileUserUuid,
        format: cardInfo?.format,
        doorIds: cardInfo?.format === CardFormat.AVL ? cardInfo?.doorIds : [],
        startDate:
          cardInfo?.format === CardFormat.AVL && cardInfo?.startDate !== defaultDateTimeUTCValue
            ? new Date(cardInfo?.startDate)
            : moment.tz(new Date(), buildingTimezone).toDate(),
        endDate:
          cardInfo?.format === CardFormat.AVL && cardInfo?.endDate !== defaultDateTimeUTCValue
            ? new Date(cardInfo?.endDate)
            : moment.tz(new Date(), buildingTimezone).toDate(),
        latchDoorIds: cardInfo?.format === CardFormat.LATCH ? cardInfo?.doorIds : [],
        latchStartDate:
          cardInfo?.format === CardFormat.LATCH && cardInfo?.startDate !== defaultDateTimeUTCValue
            ? new Date(cardInfo?.startDate)
            : moment.tz(new Date(), buildingTimezone).toDate(),
        latchEndDate:
          cardInfo?.format === CardFormat.LATCH && cardInfo?.endDate !== defaultDateTimeUTCValue
            ? new Date(cardInfo?.endDate)
            : moment.tz(new Date(), buildingTimezone).toDate()
      });
    } else {
      setGetcardResult(null);
    }
    if (buildingData) {
      setbuildingInfo(buildingData);
    } else {
      setbuildingInfo(null);
    }
  }, [buildingData, buildingTimezone, cardInfo, editUUID, methods]);

  useEffect(() => {
    if (buildingInfo) {
      setBuildingTimezone(buildingInfo.timezone);
    } else {
      setBuildingTimezone(null);
    }
  }, [buildingInfo]);

  const [allOccupants, setAllOccupants] = useState<any>([]);
  const [allUnassOccupants, setAllUnassOccupants] = useState<any>(null);

  const { refetch, data: occupantsData } = useQuery<OccupantProps[], Error>(
    'occupants',
    () => OccupantService.findAllOccupants(),
    {
      enabled: !!orgUuid,
      onError: () => {
        toast.error(generateApiFailErrorMessage('Occupants'));
      }
    }
  );

  useEffect(() => {
    if (occupantsData && orgUuid) {
      refetch();
      setAllOccupants(occupantsData);
    }
  }, [orgUuid, occupantsData, refetch]);

  const formatedOptions = (value: any, label: string) => {
    return { value: value, label: label };
  };

  /* The above code is a useEffect hook in a TypeScript React component. It is triggered whenever the
  dependencies `allOccupants` and `orgUuid` change. */
  useEffect(() => {
    if (allOccupants && Array.isArray(allOccupants) && orgUuid) {
      const populateUnassignedOccupants: { value: string; label: string }[] = [];
      allOccupants?.map(
        (occupant: {
          organizationUuid: string;
          uuid: string;
          firstName: string;
          lastName: string;
          cards: any[];
        }) =>
          occupant?.organizationUuid === orgUuid && !occupant?.cards
            ? populateUnassignedOccupants.push(
                formatedOptions(occupant?.uuid, occupant?.firstName + ' ' + occupant?.lastName)
              )
            : occupant?.organizationUuid === orgUuid &&
              occupant?.cards?.map(
                (card: {
                  status: string;
                  badgeId: number | string;
                  facilityCode: number | string;
                }) =>
                  card?.status === 'UNASSIGNED'
                    ? populateUnassignedOccupants.push(
                        formatedOptions(
                          occupant?.uuid,
                          occupant?.firstName + ' ' + occupant?.lastName
                        )
                      )
                    : card?.status === 'ASSIGNED' &&
                      card?.badgeId === getcardResult?.badgeId &&
                      card?.facilityCode === getcardResult?.facilityCode
                    ? populateUnassignedOccupants.push(
                        formatedOptions(
                          occupant?.uuid,
                          occupant?.firstName + ' ' + occupant?.lastName
                        )
                      )
                    : null
              )
      );
      setAllUnassOccupants(populateUnassignedOccupants);
    }
  }, [allOccupants, orgUuid, getcardResult]);

  useEffect(() => {
    if (orgData?.integrationDetails?.acsSystemId) {
      setAcsSystemId(orgData?.integrationDetails?.acsSystemId);
    } else {
      setAcsSystemId(undefined);
    }
  }, [orgData]);

  const selectedMobileUuid = methods.watch('mobileUserUuid');

  const { data: occupantData } = useQuery<OccupantProps, Error>(
    ['occupant', selectedMobileUuid],
    () => OccupantService.findOccupantsById(selectedMobileUuid),
    {
      enabled: !!selectedMobileUuid && !editUUID,
      onError: () => {
        toast.error(generateApiFailErrorMessage('Occupant'));
      }
    }
  );

  const integrationUuid = occupantData?.integrationUuid;

  const { data: integrationData } = useQuery<IntegrationProps, Error>(
    ['integration', integrationUuid],
    () => IntegrationService.findIntegrationById(integrationUuid),
    {
      enabled: !!integrationUuid && !editUUID,
      onError: () => {
        toast.error(generateApiFailErrorMessage('Integration'));
      }
    }
  );

  useEffect(() => {
    if (selectedMobileUuid && occupantData) {
      if (integrationData?.name === 'None') {
        setIsNoneIntegration(true);
        if (!editUUID) {
          methods.setValue('format', '');
        }
      } else {
        setIsNoneIntegration(false);
      }
    }
  }, [selectedMobileUuid, occupantData, integrationData?.name, methods, editUUID]);

  // Pre-load the card format based on the selected Occupant's integration
  useEffect(() => {
    if (selectedMobileUuid && occupantData?.integrationUuid) {
      if (integrationData && integrationData.name) {
        const formatValue = {
          bline: CardFormat.W26,
          AVL: CardFormat.AVL,
          Latch: CardFormat.LATCH
        }[integrationData.name];

        if (formatValue) {
          methods.setValue('format', formatValue);
        }
      } else {
        methods.setValue('format', '');
      }
    }
  }, [integrationData, methods, selectedMobileUuid, occupantData?.integrationUuid]);

  // Convert `CardFormat` enum to an array of objects suitable for card format dropdown options
  const enumToDropdownOptions = Object.values(CardFormat).map((value) => ({
    value,
    label: cardFormatLabels[value]
  }));

  // Filter Card Format options based on the selected Occupant's integration type
  const filterCardFormats = (integrationName: string | undefined) => {
    let options = enumToDropdownOptions;

    if (integrationName === AccessIntegrationName.Wavelynx) {
      options = options.filter((option) => option.value === CardFormat.W26);
    } else if (integrationName === AccessIntegrationName.AssaVisionline) {
      options = options.filter((option) => option.value === CardFormat.AVL);
    } else if (integrationName === AccessIntegrationName.Latch) {
      options = options.filter((option) => option.value === CardFormat.LATCH);
    } else if (integrationName === AccessIntegrationName.None) {
      options = [];
    }
    return options;
  };

  // Fetch visionline doors
  const { data: doorsData } = useQuery<SeamVisionlineDoorsProps[], Error>(
    ['doors', acsSystemId, selectedCardFormat === CardFormat.AVL],
    () => SeamAssaVisionlineService.findAllVisionlineDoors(acsSystemId),
    {
      enabled: selectedCardFormat === CardFormat.AVL && !!acsSystemId,
      onError: () => {
        toast.error(generateApiFailErrorMessage('Doors'));
      }
    }
  );

  useEffect(() => {
    if (doorsData) {
      setVisionlineDoorsData(doorsData);
    } else {
      setVisionlineDoorsData(null);
    }
  }, [doorsData]);

  useEffect(() => {
    if (visionlineDoorsData) {
      const formattedDoors = FormatVisionlineDoorsTreeData(visionlineDoorsData);
      setDoorOptions(formattedDoors); // Update the state with the new data
    } else {
      setDoorOptions([]); // Set to empty array if there is no data
    }
  }, [visionlineDoorsData]);

  // Fetch Latch Doors
  const { data: latchDoors } = useQuery<LatchDoorProps, Error>(
    ['latchDoors', orgUuid, selectedCardFormat === CardFormat.LATCH],
    () => LatchService.retrieveDoors(orgUuid),
    {
      enabled: selectedCardFormat === CardFormat.LATCH && !!orgUuid,
      onError: () => {
        toast.error('Please select an organization with Latch integration to see door options.');
      }
    }
  );

  useEffect(() => {
    if (latchDoors) {
      setLatchDoorsData(latchDoors);
    } else {
      setLatchDoorsData(null);
    }
  }, [latchDoors]);

  useEffect(() => {
    if (latchDoorsData) {
      const formattedDoors = FormatLatchDoorsTreeData(latchDoorsData);
      setLatchDoorOptions(formattedDoors); // Update the state with the new data
    } else {
      setLatchDoorOptions([]); // Set to empty array if there is no data
    }
  }, [latchDoorsData]);

  const saveCardMutation = useSaveCard({
    createQuery,
    updateQuery,
    queryName,
    onSuccess: () => {
      setShowModal(false);
      setDisabled(false);
      if (!editUUID) {
        toast.success(generateSaveSuccessMessage('Card'));
      } else {
        toast.success(generateUpdateSuccessMessage('Card'));
      }
      if (setEditUUID) setEditUUID('');
    },
    onError: (error: any) => {
      setDisabled(false);
      if (error.response) {
        const errorMessage =
          error.response.data?.error ||
          error.response.data?.message ||
          'An unexpected error occurred.';

        switch (error.response.status) {
          case 400: // Bad Request, showing the backend provided message
            // Display the error message for longer duration (15 seconds (15000 milliseconds)) when they are client side errors
            toast.error(errorMessage, { autoClose: 15000 });
            break;
          default:
            if (!editUUID) {
              toast.error(generateSaveErrorMessage('Card'));
            } else {
              toast.error(generateUpdateErrorMessage('Card'));
            }
            break;
        }
      } else {
        // Handle cases where the error is not from the server response
        toast.error(GetNonServerErrorMessage);
      }
    }
  });

  const handleRevoke = () => {
    const data = getcardResult && {
      ...getcardResult,
      mobileUserUuid: undefined
    };
    if (data) {
      saveCardMutation.mutate({
        uuid: data?.uuid,
        description: data?.description,
        buildingUuid: data?.buildingUuid,
        organizationUuid: data?.organizationUuid,
        badgeId: data?.badgeId,
        facilityCode: data?.facilityCode,
        format: data?.format,
        mobileUserUuid: data?.mobileUserUuid,
        doorIds: data?.doorIds,
        startDate:
          data?.startDate !== defaultDateTimeUTCValue
            ? formatRFC3339(new Date(data?.startDate))
            : moment.tz(new Date(), buildingTimezone).toDate(),
        endDate:
          data?.endDate !== defaultDateTimeUTCValue
            ? formatRFC3339(new Date(data?.endDate))
            : moment.tz(new Date(), buildingTimezone).toDate()
      });
    }
  };

  // Watch method to track the selected 'assaCardType' filed value
  const assaCardTypeWatch = methods.watch('assaCardType');
  const assaGuestCardTypeWatch = methods.watch('assaGuestCardType');

  useEffect(() => {
    if (assaCardTypeWatch == AssaGuestCardTypeValue) {
      setIsGuestAssaCardType(true);
    } else {
      setIsGuestAssaCardType(false);
    }
  }, [assaCardTypeWatch]);

  /* The below code is using the `watch` method to monitor changes to the 'format' property. 
  When the 'format' property changes, the component will update the selected card format based on the value of the 'format' property. */
  const cardFormatWatch = methods.watch('format');

  useEffect(() => {
    switch (cardFormatWatch) {
      case CardFormat.W26:
        setSelectedCardFormat(CardFormat.W26);
        break;
      case CardFormat.AVL:
        setSelectedCardFormat(CardFormat.AVL);
        break;
      case CardFormat.LATCH:
        setSelectedCardFormat(CardFormat.LATCH);
        break;
      default:
        setSelectedCardFormat(null);
    }
  }, [cardFormatWatch]);

  const onSubmit = (e: CardProps) => {
    setDisabled(true);
    let selectedDoorIds: string[] = [];
    let selectedStartDate: string | null = null;
    let selectedEndDate: string | null = null;

    // Ensure selectedCardFormat is not null before proceeding
    if (selectedCardFormat !== null) {
      // Map the correct values based on the selected card format
      const doorIdMappings: Partial<Record<CardFormat, () => string[]>> = {
        [CardFormat.AVL]: () => e.doorIds || [],
        [CardFormat.LATCH]: () => e.latchDoorIds || []
      };

      // Map the correct values based on the selected card format
      const dateMappings: Partial<
        Record<CardFormat, { startDate: () => string | null; endDate: () => string | null }>
      > = {
        [CardFormat.AVL]: {
          startDate: () => (e.startDate ? formatRFC3339(e.startDate) : null),
          endDate: () => (e.endDate ? formatRFC3339(e.endDate) : null)
        },
        [CardFormat.LATCH]: {
          startDate: () => (e.latchStartDate ? formatRFC3339(e.latchStartDate) : null),
          endDate: () => (e.latchEndDate ? formatRFC3339(e.latchEndDate) : null)
        }
      };

      // Use the mappings if they exist for the selected card format
      selectedDoorIds = doorIdMappings[selectedCardFormat]?.() || [];
      selectedStartDate = dateMappings[selectedCardFormat]?.startDate() || null;
      selectedEndDate = dateMappings[selectedCardFormat]?.endDate() || null;
    }

    if (orgData?.buildingUuid) {
      saveCardMutation.mutate({
        uuid: e?.uuid,
        description: e?.description,
        buildingUuid: orgData?.buildingUuid,
        organizationUuid: e?.organizationUuid,
        mobileUserUuid: e?.mobileUserUuid ? e?.mobileUserUuid : undefined,
        badgeId: typeof e?.badgeId === 'string' ? parseInt(e?.badgeId) : e?.badgeId,
        facilityCode:
          typeof e?.facilityCode === 'string' ? parseInt(e?.facilityCode) : e?.facilityCode,
        format: e?.format,
        doorIds: selectedDoorIds,
        startDate: selectedStartDate,
        endDate: selectedEndDate,
        assaCardType: e?.assaCardType,
        assaGuestCardType: e?.assaGuestCardType
      });
    }
  };

  return !editUUID || isSuccess ? (
    <>
      <Modal
        isOpen={showModal}
        onRequestClose={() => {
          setShowModal(false);
          if (setEditUUID) setEditUUID('');
        }}
        title={entityName}
      >
        <form onSubmit={methods.handleSubmit(onSubmit)} className={`text-left items-start`}>
          <div className={'grid grid-cols-1 sm:grid-cols-2 gap-4 mr-8'}>
            <SelectEntity
              Controller={Controller}
              control={methods.control}
              label="Choose an Organization"
              placeholder="Select Organization"
              queryname="organizations"
              name="organizationUuid"
              errors={methods.formState.errors}
              disabled={editUUID && true}
              query={OrganizationService.findAllOrgs}
              defaultValue={getcardResult?.organizationUuid}
            />
            <SelectInput
              Controller={Controller}
              control={methods.control}
              label="Assign Card to an Occupant"
              name="mobileUserUuid"
              errors={methods.formState.errors}
              placeholder="Select Occupant"
              options={allUnassOccupants ? allUnassOccupants : []}
            />
            <SelectInput
              Controller={Controller}
              control={methods.control}
              label="Card Format"
              name="format"
              defaultValue={getcardResult?.format}
              errors={methods.formState.errors}
              placeholder="Select a Format"
              options={filterCardFormats(integrationData?.name)}
              disabled={!!editUUID}
            />
            <Input
              label="Description"
              name="description"
              type="text"
              placeholder="Bline"
              autoFocus
              methods={methods}
            />
            {selectedCardFormat === CardFormat.W26 && (
              <>
                <GeneratorInput
                  label="Badge ID"
                  placeholder="12345"
                  validate={{
                    withinRange: (value: number) => {
                      return (
                        (buildingInfo &&
                          !!value &&
                          value <= buildingInfo.maxBadge &&
                          value >= buildingInfo.minBadge) ||
                        `Badge id must be between ${buildingInfo && buildingInfo.minBadge} and ${
                          buildingInfo && buildingInfo.maxBadge
                        }`
                      );
                    }
                  }}
                  width={320}
                  min={buildingInfo?.minBadge || 0}
                  max={buildingInfo?.maxBadge || 0}
                  excluded={cardsData && cardsData?.map((card) => card?.badgeId)}
                  methods={methods}
                  name="badgeId"
                />

                <GeneratorInput
                  label="Facility Id"
                  placeholder="12345"
                  validate={{
                    withinRange: (value: number) => {
                      return (
                        (buildingInfo &&
                          !!value &&
                          value <= buildingInfo.maxFac &&
                          value >= buildingInfo.minFac) ||
                        `Badge id must be between ${buildingInfo && buildingInfo.minFac} and ${
                          buildingInfo && buildingInfo.maxFac
                        }`
                      );
                    }
                  }}
                  width={320}
                  min={buildingInfo?.minFac || 0}
                  max={buildingInfo?.maxFac || 0}
                  excluded={[]}
                  methods={methods}
                  name="facilityCode"
                />
              </>
            )}
          </div>

          {selectedCardFormat === CardFormat.AVL && (
            <>
              <div className="col-span-2 w-auto mt-3 mr-8">
                <NestedAntdMultiSelect
                  Controller={Controller}
                  control={methods.control}
                  name="doorIds"
                  label="Choose Doors"
                  placeholder="Select Doors"
                  options={Object.values(doorOptions)}
                  errors={methods.formState.errors}
                  initialValues={
                    selectedCardFormat === CardFormat.AVL && doorOptions && doorOptions.length > 0
                      ? getcardResult?.doorIds
                      : undefined
                  }
                />
              </div>
              <div className="col-span-2 mt-3 mr-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <TimezoneDateTimeRangePicker
                    Controller={Controller}
                    control={methods.control}
                    label="Check-In Date & Time"
                    name="startDate"
                    placeholder="Select Check-In Date & Time"
                    isClearable={false}
                    timezone={buildingTimezone}
                    selectsStart={false}
                    startDate={methods.watch('startDate')}
                    endDate={methods.watch('endDate')}
                    maxDate={moment.tz(new Date(), buildingTimezone).toDate()}
                    errors={methods.formState.errors}
                  />
                  <TimezoneDateTimeRangePicker
                    Controller={Controller}
                    control={methods.control}
                    label="Check-Out Date & Time"
                    name="endDate"
                    placeholder="Select Check-Out Date & Time"
                    isClearable={false}
                    timezone={buildingTimezone}
                    minDate={methods.watch('startDate')}
                    selectsEnd={true}
                    startDate={methods.watch('startDate')}
                    endDate={methods.watch('endDate')}
                    errors={methods.formState.errors}
                  />
                  <SelectInput
                    Controller={Controller}
                    control={methods.control}
                    label="Select Assa Card Type"
                    name="assaCardType"
                    errors={methods.formState.errors}
                    placeholder="Select Assa Card Type"
                    options={assaCardTypes}
                  />
                  {isGuestAssaCardType && (
                    <SelectInput
                      Controller={Controller}
                      control={methods.control}
                      label="Select Guest Card Type"
                      name="assaGuestCardType"
                      errors={methods.formState.errors}
                      placeholder="Select Guest Card Type"
                      options={assaGuestCardTypes}
                    />
                  )}
                </div>
              </div>
            </>
          )}

          {selectedCardFormat === CardFormat.LATCH && (
            <>
              <div className="col-span-2 w-auto mt-3 mr-8">
                <NestedAntdMultiSelect
                  Controller={Controller}
                  control={methods.control}
                  name="latchDoorIds"
                  label="Choose Doors"
                  placeholder="Select Doors"
                  options={Object.values(latchDoorOptions)}
                  errors={methods.formState.errors}
                  initialValues={
                    selectedCardFormat === CardFormat.LATCH &&
                    latchDoorOptions &&
                    latchDoorOptions.length > 0
                      ? getcardResult?.doorIds
                      : undefined
                  }
                />
              </div>
              <div className="col-span-2 mt-3 mr-8">
                <div className="grid grid-cols-2 gap-4">
                  <TimezoneDateTimeRangePicker
                    Controller={Controller}
                    control={methods.control}
                    label="Check-In Date & Time"
                    name="latchStartDate"
                    placeholder="Select Check-In Date & Time"
                    isClearable={false}
                    timezone={buildingTimezone}
                    selectsStart={false}
                    startDate={methods.watch('latchStartDate')}
                    endDate={methods.watch('latchEndDate')}
                    errors={methods.formState.errors}
                  />
                  <TimezoneDateTimeRangePicker
                    Controller={Controller}
                    control={methods.control}
                    label="Check-Out Date & Time"
                    name="latchEndDate"
                    placeholder="Select Check-Out Date & Time"
                    isClearable={false}
                    timezone={buildingTimezone}
                    minDate={methods.watch('latchStartDate')}
                    selectsEnd={true}
                    startDate={methods.watch('latchStartDate')}
                    endDate={methods.watch('latchEndDate')}
                    errors={methods.formState.errors}
                  />
                </div>
              </div>
            </>
          )}

          <div className="flex gap-4">
            {getcardResult?.status === 'ASSIGNED' && selectedCardFormat === CardFormat.W26 && (
              <PrimaryButton
                type="button"
                buttonLabel="Revoke Card"
                onClick={() => {
                  handleRevoke();
                }}
                iconLeft={<CancelIcon />}
                variant="default"
                size={'!bg-secondary2 mt-4 px-6 w-auto gap-2'}
              />
            )}
            <PrimaryButton
              buttonLabel={entityName}
              iconLeft={<CardsIcon />}
              variant="default"
              size={'mt-4 px-6 w-auto gap-2'}
              disabled={disabled || isNoneIntegration}
            />
          </div>
          {isGuestAssaCardType && (
            <>
              {assaGuestCardTypeWatch === AssaGuestOverrideCardTypeValue ? (
                <div className="text-sm text-neutral6 mt-4 p-2 border border-neutral3 bg-neutral1 rounded w-[550px]">
                  <p className="mb-2">
                    <span className="font-semibold">Please Note: </span>
                    Selecting 'Override' will deactivate existing access credentials for the chosen
                    doors and replace them with new ones.
                    <br />
                    <br />
                    If you intend to generate an 'Override' Assa Guest card of 'Assa Visionline'
                    card format, it is imperative that the Occupant has completed sign-in procedures
                    on the B-Line mobile application to register their phone before creating the
                    card.
                  </p>
                </div>
              ) : assaGuestCardTypeWatch === AssaGuestJoinerCardTypeValue ? (
                <div className="text-sm text-neutral6 mt-4 p-2 border border-neutral3 bg-neutral1 rounded w-[550px]">
                  <p className="mb-2">
                    <span className="font-semibold">Please Note: </span>
                    Selecting 'Joiner' will preserve existing access credentials for the chosen
                    doors while also adding new ones.
                  </p>
                </div>
              ) : null}
            </>
          )}
          {isNoneIntegration && selectedMobileUuid && (
            <div className="text-sm text-secondary2 mt-4 p-2 border border-secondary2 bg-[#fff7f6] rounded w-[550px]">
              Card creation is not possible for the selected occupant because they have no
              integration assigned. Please select an occupant with a valid integration.
            </div>
          )}
        </form>
      </Modal>
    </>
  ) : (
    <LoadingSpinner />
  );
}
